import { useState, useEffect } from 'react';
import type { DashboardStats } from '@cowork-gen/core';
import { api, wsClient } from '../api/client';

export function StatsCard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 10000);

    // Subscribe to real-time stats updates
    const unsubscribe = wsClient.on('stats', (data) => {
      const statsData = data as Partial<DashboardStats>;
      setStats((prev) => prev ? { ...prev, ...statsData } : null);
    });

    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, []);

  async function loadStats() {
    try {
      const response = await api.getStats();
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading || !stats) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          </div>
        ))}
      </div>
    );
  }

  const cards = [
    { label: 'Total Tasks', value: stats.totalTasks, color: 'text-gray-900' },
    { label: 'Pending', value: stats.pendingTasks, color: 'text-yellow-600' },
    { label: 'Running', value: stats.runningTasks, color: 'text-blue-600' },
    { label: 'Completed', value: stats.completedTasks, color: 'text-green-600' },
    { label: 'Failed', value: stats.failedTasks, color: 'text-red-600' },
    { label: 'Active Agents', value: stats.activeAgents, color: 'text-purple-600' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {cards.map((card) => (
        <div key={card.label} className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">{card.label}</p>
          <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
        </div>
      ))}
    </div>
  );
}
